<template>
  <div class="landing-pricing-toggle-bar">
    <div class="landing-pricing-toggle-bar__controls">
      <KdsToggle
        :model-value="modelValue"
        :options="toggleOptions"
        :aria-label="t('landing.pricing.billingCycleAriaLabel')"
        @update:model-value="onToggle"
      />
      <Transition name="badge-fade">
        <KdsBadge v-if="modelValue === 'yearly'" tone="ok" class="landing-pricing-toggle-bar__badge">
          {{ t('landing.pricing.toggleBar.badge2MonthsIncluded') }}
        </KdsBadge>
      </Transition>
    </div>
    <Transition name="explanation-fade">
      <p v-if="modelValue === 'yearly'" class="landing-pricing-toggle-bar__explanation">
        {{ t('landing.pricing.toggleBar.yearlyExplanation') }}
      </p>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import KdsToggle from '@/kds/primitives/KdsToggle.vue';
import KdsBadge from '@/kds/primitives/KdsBadge.vue';
import type { BillingCycle } from '@/config/pricing';

type Props = {
  modelValue: BillingCycle;
};

defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: BillingCycle): void;
}>();

const { t } = useI18n();

const toggleOptions = computed(() => [
  { value: 'monthly', label: t('landing.pricing.toggleBar.monthly') },
  { value: 'yearly', label: t('landing.pricing.toggleBar.yearly') }
]);

const onToggle = (val: string) => {
  if (val === 'monthly' || val === 'yearly') {
    emit('update:modelValue', val);
  }
};
</script>

<style scoped lang="scss">
.landing-pricing-toggle-bar {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);

  &__controls {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
    justify-content: center;
  }

  &__badge {
    flex-shrink: 0;
  }

  &__explanation {
    margin: 0;
    font-size: var(--fs-body-sm);
    color: var(--stone);
    text-align: center;
    font-family: var(--font-ui);
  }
}

// Badge transition
.badge-fade-enter-active,
.badge-fade-leave-active {
  transition: opacity 150ms var(--ease-out), transform 150ms var(--ease-out);
}

.badge-fade-enter-from,
.badge-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

// Explanation transition
.explanation-fade-enter-active,
.explanation-fade-leave-active {
  transition: opacity 150ms var(--ease-out), transform 150ms var(--ease-out);
}

.explanation-fade-enter-from,
.explanation-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

@media (prefers-reduced-motion: reduce) {
  .badge-fade-enter-active,
  .badge-fade-leave-active,
  .explanation-fade-enter-active,
  .explanation-fade-leave-active {
    transition: none !important;
  }
}
</style>
